import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { apiJson } from '../auth/session.ts';
import { Icon } from './widgets.tsx';

export type IdeaKind = 'idea' | 'bug';
export type IdeaStatus = 'open' | 'solved';

export interface IdeaItem {
  n: number;
  id: string;
  kind: IdeaKind;
  title: string;
  body: string;
  status: IdeaStatus;
  writeup: string;
  solvedAt: string | null;
  author: string;
  updatedAt: string;
  updatedBy: string;
}

const OPEN_KEY = 'ple:ideasOpen';

function loadOpen(): boolean {
  try {
    return globalThis.localStorage?.getItem(OPEN_KEY) === '1';
  } catch {
    return false;
  }
}

function itemStatus(item: IdeaItem): IdeaStatus {
  return item.status === 'solved' ? 'solved' : 'open';
}

export function IdeasDock() {
  const [open, setOpen] = useState(loadOpen);
  const [items, setItems] = useState<IdeaItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | IdeaKind>('all');
  const [bucket, setBucket] = useState<'open' | 'solved' | 'all'>('open');
  const [kind, setKind] = useState<IdeaKind>('idea');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editBody, setEditBody] = useState('');
  const [editKind, setEditKind] = useState<IdeaKind>('idea');

  const reload = useCallback(async () => {
    const data = await apiJson<{ items: IdeaItem[] }>('GET', '/api/ideas');
    setItems(data.items);
    setError(null);
  }, []);

  useEffect(() => {
    if (!open) return;
    reload().catch((err: unknown) => setError(err instanceof Error ? err.message : 'Could not load ideas'));
    const t = window.setInterval(() => {
      reload().catch(() => undefined);
    }, 15000);
    return () => clearInterval(t);
  }, [open, reload]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    try {
      localStorage.setItem(OPEN_KEY, next ? '1' : '0');
    } catch {
      /* ignore */
    }
  };

  const post = async () => {
    const t = title.trim();
    if (!t || busy) return;
    setBusy(true);
    setError(null);
    try {
      await apiJson('POST', '/api/ideas', { kind, title: t, body });
      setTitle('');
      setBody('');
      setBucket('open');
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not post');
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async (id: string) => {
    const t = editTitle.trim();
    if (!t || busy) return;
    setBusy(true);
    setError(null);
    try {
      await apiJson('PATCH', `/api/ideas/${encodeURIComponent(id)}`, { kind: editKind, title: t, body: editBody });
      setEditing(null);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    if (!confirm('Delete this item?')) return;
    setBusy(true);
    setError(null);
    try {
      await apiJson('DELETE', `/api/ideas/${encodeURIComponent(id)}`);
      if (editing === id) setEditing(null);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete');
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (item: IdeaItem) => {
    setEditing(item.id);
    setEditTitle(item.title);
    setEditBody(item.body);
    setEditKind(item.kind);
  };

  const matching = (items ?? []).filter((item) => {
    if (filter !== 'all' && item.kind !== filter) return false;
    if (bucket !== 'all' && itemStatus(item) !== bucket) return false;
    return true;
  });
  const openItems = matching.filter((item) => itemStatus(item) === 'open');
  const solvedItems = matching.filter((item) => itemStatus(item) === 'solved');

  const renderItem = (item: IdeaItem) => (
    <article key={item.id} className={`ideas-item ${item.kind} ${itemStatus(item)}`}>
      {editing === item.id ? (
        <>
          <div className="ideas-item-head">
            <span className="ideas-n">{item.n}</span>
            <div className="ideas-kinds">
              <button className={`chip ${editKind === 'idea' ? 'active' : ''}`} onClick={() => setEditKind('idea')}>Idea</button>
              <button className={`chip ${editKind === 'bug' ? 'active' : ''}`} onClick={() => setEditKind('bug')}>Bug</button>
            </div>
          </div>
          <input className="text" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} />
          <textarea className="text ideas-body" rows={4} value={editBody} onChange={(e) => setEditBody(e.target.value)} />
          <div className="ideas-item-actions">
            <button className="chip active" disabled={busy || !editTitle.trim()} onClick={() => void saveEdit(item.id)}>Save</button>
            <button className="chip" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </>
      ) : (
        <>
          <div className="ideas-item-head">
            <span className="ideas-n">{item.n}</span>
            <span className={`ideas-tag ${item.kind}`}>{item.kind}</span>
            {itemStatus(item) === 'solved' ? <span className="ideas-tag solved">solved</span> : null}
            <b>{item.title}</b>
          </div>
          {item.body.trim() ? <p>{item.body}</p> : null}
          {itemStatus(item) === 'solved' && item.writeup.trim() ? (
            <div className="ideas-writeup">
              <span>How it was solved</span>
              <p>{item.writeup}</p>
            </div>
          ) : null}
          <div className="ideas-meta">
            {item.author}
            {item.updatedBy !== item.author ? ` · edited by ${item.updatedBy}` : ''}
            {item.updatedAt ? ` · ${new Date(item.updatedAt).toLocaleString()}` : ''}
          </div>
          <div className="ideas-item-actions">
            <button className="chip" onClick={() => startEdit(item)}><Icon name="pencil" size={14} /> Edit</button>
            <button className="chip" onClick={() => void remove(item.id)}><Icon name="trash" size={14} /></button>
          </div>
        </>
      )}
    </article>
  );

  const listBody = (): ReactNode => {
    if (items === null && !error) return <div className="muted small">Loading…</div>;
    if (items && !matching.length) return <div className="muted small">{bucket === 'solved' ? 'Nothing solved yet.' : 'Nothing here yet.'}</div>;
    if (bucket === 'all') {
      return (
        <>
          {openItems.length ? <div className="ideas-section">Open</div> : null}
          {openItems.map(renderItem)}
          {solvedItems.length ? <div className="ideas-section">Solved</div> : null}
          {solvedItems.map(renderItem)}
        </>
      );
    }
    return matching.map(renderItem);
  };

  return (
    <div className={`ideas-dock ${open ? 'open' : ''}`}>
      {open && (
        <div className="ideas-panel">
          <div className="ideas-head">
            <b>Ideas & bugs</b>
            <span className="muted small">Numbered list · tell an agent “Fix idea 3”</span>
            <button className="icon-btn" title="Close" onClick={toggle}><Icon name="close" size={16} /></button>
          </div>
          <div className="ideas-compose">
            <div className="ideas-kinds">
              <button className={`chip ${kind === 'idea' ? 'active' : ''}`} onClick={() => setKind('idea')}><Icon name="bulb" size={14} /> Idea</button>
              <button className={`chip ${kind === 'bug' ? 'active' : ''}`} onClick={() => setKind('bug')}><Icon name="bug" size={14} /> Bug</button>
            </div>
            <input className="text" placeholder={kind === 'bug' ? 'What is broken?' : 'What should we add?'} value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void post(); } }} />
            <textarea className="text ideas-body" rows={3} placeholder="Details (optional)" value={body} onChange={(e) => setBody(e.target.value)} />
            <button className="chip active" disabled={busy || !title.trim()} onClick={() => void post()}>Post</button>
          </div>
          <div className="ideas-kinds">
            <button className={`chip ${bucket === 'open' ? 'active' : ''}`} onClick={() => setBucket('open')}>Open</button>
            <button className={`chip ${bucket === 'solved' ? 'active' : ''}`} onClick={() => setBucket('solved')}>Solved</button>
            <button className={`chip ${bucket === 'all' ? 'active' : ''}`} onClick={() => setBucket('all')}>All</button>
          </div>
          <div className="ideas-kinds">
            <button className={`chip ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>All kinds</button>
            <button className={`chip ${filter === 'idea' ? 'active' : ''}`} onClick={() => setFilter('idea')}>Ideas</button>
            <button className={`chip ${filter === 'bug' ? 'active' : ''}`} onClick={() => setFilter('bug')}>Bugs</button>
          </div>
          {error && <div className="ideas-error">{error}</div>}
          <div className="ideas-list">
            {listBody()}
          </div>
        </div>
      )}
      <button className={`ideas-fab ${open ? 'on' : ''}`} title={open ? 'Close ideas' : 'Ideas & bugs'} onClick={toggle}>
        <Icon name="bulb" size={22} />
      </button>
    </div>
  );
}
